import { createSlice } from '@reduxjs/toolkit';
import { difference, union } from 'es-toolkit';

import { fetchItemShippingEstimate } from '@/redux/modules/shipping/shipping.actions';
import { defaultShippingSlice, HiddenQuoteReason } from '@/redux/modules/shipping/shipping.types';
import { ShippingVisibility } from '@/types/ShippingEstimate';

const shippingSlice = createSlice({
    extraReducers: (builder) => {
        builder.addCase(fetchItemShippingEstimate.pending, (slice, { meta }) => {
            slice.loading = union(slice.loading, [meta.arg.itemId]);

            // clear out previous error if an error occurred. That way, new/repeated errors will behave properly
            if (typeof slice.byId[meta.arg.itemId] === 'string') {
                slice.byId[meta.arg.itemId] = null;
            }
        });
        builder.addCase(fetchItemShippingEstimate.fulfilled, (slice, { meta, payload }) => {
            slice.loading = difference(slice.loading, [meta.arg.itemId]);
            slice.loaded[meta.arg.itemId] = Date.now();

            const { estimate, isInAlwaysShowExpensiveQuoteExperiment } = payload;

            if (
                estimate?.shippingAvailable ||
                estimate?.visibility === ShippingVisibility.Pending ||
                (estimate?.visibility === ShippingVisibility.Uncalculated && !estimate?.message) ||
                estimate?.visibility === ShippingVisibility.Unavailable ||
                (!meta.arg.countryCode && !meta.arg.postalCode)
            ) {
                // normal quote without any potential issues
                slice.byId[meta.arg.itemId] = estimate;
            } else if (estimate?.message === 'invalid zip code') {
                // quote that had a bad zip code
                slice.byId[meta.arg.itemId] = HiddenQuoteReason.BadZipCode;
            } else {
                // quote that would be too expensive to show normally.
                // we need to check if the current user is allowed to view it.
                slice.byId[meta.arg.itemId] = isInAlwaysShowExpensiveQuoteExperiment
                    ? { ...estimate, message: HiddenQuoteReason.TooExpensive }
                    : HiddenQuoteReason.TooExpensive;
            }
        });
        builder.addCase(fetchItemShippingEstimate.rejected, (slice, { meta, payload: status }) => {
            slice.loading = difference(slice.loading, [meta.arg.itemId]);
            slice.loaded[meta.arg.itemId] = Date.now();
            let errorType: HiddenQuoteReason;
            switch (status) {
                case 400:
                    errorType = HiddenQuoteReason.MissingDimensions;
                    break;
                default:
                    errorType = HiddenQuoteReason.SystemOrPartnerError;
                    break;
            }
            slice.byId[meta.arg.itemId] = errorType;
        });
    },
    initialState: defaultShippingSlice,
    name: 'shipping',
    reducers: {},
});

export const { reducer: shippingReducer } = shippingSlice;
